import { api } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader } from '../../components/PageHeader';
import { BinaryTreeView } from '../../components/tree/BinaryTreeView';
import { formatNumber } from '../../lib/format';
import { Button, Card, EmptyState, Spinner, StatCard, StatCards } from '../../ui';
import { TEAM_TABS } from './DirectTeam';

/** Depth rendered per fetch — the backend caps at 6; 4 keeps each request small
 *  (at most 2^4 - 1 = 15 nodes) while still reading as a real tree. Drilling
 *  into a node re-roots the same query deeper rather than loading everything
 *  flat/unbounded. */
const RENDER_DEPTH = 4;

interface TrailEntry {
  userId: string;
  code: string;
}

/**
 * The binary placement tree. This is a second, independent tree over the same
 * members — it drives Achievement Rewards only. Referral, level and club income
 * all follow the unilevel sponsor tree instead.
 *
 * Always scoped to the logged-in member's own subtree: `root` is only ever the
 * caller or one of the caller's binary descendants (drilling down via
 * `onDrill`), which the backend re-verifies on every request — a member can
 * never navigate to an arbitrary other member's tree here.
 */
export function BinaryTree() {
  const [trail, setTrail] = useState<TrailEntry[]>([]);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const currentRoot = trail.length > 0 ? trail[trail.length - 1].userId : undefined;

  const { data: root, isLoading, isFetching } = useQuery({
    queryKey: ['binary-tree', currentRoot],
    queryFn: () => api.team.binaryTree(currentRoot, RENDER_DEPTH).then((r) => r.data),
  });

  const legs = useQuery({
    queryKey: ['binary-legs'],
    queryFn: () => api.team.binaryLegCounts().then((r) => r.data),
  });

  const handleDrill = (node: { user_id: string; user_code: string }) => {
    setPendingId(node.user_id);
    setTrail((t) => [...t, { userId: node.user_id, code: node.user_code }]);
  };

  if (!isFetching && pendingId) setPendingId(null);

  return (
    <>
      <PageHeader
        title="Binary Tree"
        description="Left/right placement. Achievement Rewards pay on the matched pair. Hover (or tap) a member for details."
        tabs={TEAM_TABS}
      />

      <StatCards>
        <StatCard label="Left Leg" value={formatNumber(legs.data?.left_total)} />
        <StatCard label="Right Leg" value={formatNumber(legs.data?.right_total)} />
        <StatCard label="Matched" value={formatNumber(legs.data?.matched)} />
      </StatCards>

      {trail.length > 0 && (
        <div className="flex gap-1 mb-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outline-gold" size="sm" onClick={() => setTrail([])}>
            My Tree
          </Button>
          {trail.map((t, i) => (
            <Button
              key={t.userId}
              variant={i === trail.length - 1 ? 'gold' : 'ghost'}
              size="sm"
              onClick={() => setTrail((cur) => cur.slice(0, i + 1))}
            >
              {t.code}
            </Button>
          ))}
        </div>
      )}

      <Card>
        {isLoading ? (
          <Spinner />
        ) : root ? (
          <BinaryTreeView root={root} onDrill={handleDrill} pendingDrillId={pendingId} />
        ) : (
          <EmptyState title="No placement yet" />
        )}
      </Card>
    </>
  );
}
